import { useState } from 'react';
import { chevronDownIcon } from '@progress/kendo-svg-icons';
import { MultiSelect as KendoMultiSelect } from '@progress/kendo-react-dropdowns';
import { InputPrefix, InputSuffix } from '@progress/kendo-react-inputs';
import { filterBy } from '@progress/kendo-data-query';
import { SvgIcon } from '../../SvgIcon/SvgIcon';

/**
 * @component
 * @see {@link https://www.telerik.com/kendo-react-ui/components/dropdowns/multiselect| KendoReact MultiSelect Documentation}
 *
 * @description Reusable Kendo MultiSelect component.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.data - Array of items to select from.
 * @param {Array} props.value - Array of selected items.
 * @param {Function} props.onChange - Callback function called when selection changes.
 * @param {string} props.textField - Field name for the text content of each item.
 * @param {string} props.dataItemKey - Field name for the unique identifier of each item.
 * @param {string} props.placeholder - Placeholder text for the input.
 * @param {boolean} props.disabled - Whether the component is disabled.
 * @param {Function} props.itemRender - Custom render function for each item.
 * @param {string} props.label - Label for the component.
 * @param {boolean} props.autoClose - Whether to close the dropdown on item select.
 * @param {boolean} props.filterable - Whether the component is filterable.
 *
 * @example
 * <MultiSelect
 *   data={[{ id: 1, name: 'Option 1' }, { id: 2, name: 'Option 2' }]}
 *   value={selected}
 *   onChange={handleChange}
 *   textField="name"
 *   dataItemKey="id"
 *   placeholder="Select options"
 * />
 */
export const MultiSelect = ({
  data = [],
  value = [],
  onChange,
  dataItemKey = '',
  placeholder = '',
  disabled = false,
  itemRender,
  autoClose = false,
  filterable = true,
  label = null,
  prefix = null,
  suffix = <SvgIcon icon={chevronDownIcon} size="small" themeColor="dark" />, // default downward chevron
  ...rest
}) => {
  const [filteredData, setFilteredData] = useState(data);

  const handleFilterChange = (event) => {
    setFilteredData(filterBy(data.slice(), event.filter));
  };

  return (
    <KendoMultiSelect
      data={filterable ? filteredData : data}
      value={value}
      onChange={onChange}
      dataItemKey={dataItemKey}
      placeholder={placeholder}
      disabled={disabled}
      {...(itemRender && { itemRender })}
      autoClose={autoClose}
      label={label}
      filterable={filterable}
      onFilterChange={filterable ? handleFilterChange : undefined}
      prefix={prefix ? <InputPrefix orientation="horizontal">{prefix}</InputPrefix> : null}
      suffix={suffix ? <InputSuffix orientation="horizontal">{suffix}</InputSuffix> : null}
      {...rest}
    />
  );
};